// Provisional (Phase 1) copy. Final language arrives in Phase 3.

export const CAP_IDS = ["engine", "stewardship", "breakpoint", "mesh"];

export const CAPABILITIES = {
  engine: {
    id: "engine", index: "01", name: "Ordo Engine", responsibility: "Present judgment", family: "ordo", hue: "#4f7fe6", path: "/ordo/engine",
    statement: "Examines a present consequential situation and clarifies what movement is warranted.",
    concerns: "Situations in which something consequential is at stake now: what is known, who holds authority, and what movement the situation actually warrants.",
    matters: "Consequential decisions are increasingly shaped by capable intelligence. Engine is designed around making that judgment examinable and answerable, not merely fast.",
    alone: "Can be considered on its own within the Ordo software family.",
    ordoRelation: "An applied expression of General Ordo, principally its evidence, judgment, authority, and action responsibilities.",
    pairs: {
      stewardship: "Stewardship carries forward what Engine establishes, and returns it for reconsideration when circumstances change.",
      breakpoint: "Breakpoint can recognise when a situation has become consequential enough to examine.",
      mesh: "Mesh can connect the people who take part in carrying a judgment out.",
    },
    signature: {
      type: "sequence", title: "Present judgment",
      steps: [
        { label: "Present situation", text: "A consequential situation as it stands now." },
        { label: "Examination", text: "Evidence, authority, and consequence examined together." },
        { label: "Warranted movement", text: "What movement the situation actually warrants." },
      ],
    },
    placeholder: { label: "Engine and Stewardship relationship", note: "How present judgment and continuing standing connect" },
  },
  stewardship: {
    id: "stewardship", index: "02", name: "Ordo Stewardship", responsibility: "Continuing standing", family: "ordo", hue: "#7c9beb", path: "/ordo/stewardship",
    statement: "Keeps established arrangements answerable as circumstances change, recognising when they retain or lose standing.",
    concerns: "Decisions, delegations, and arrangements that were sound when established, and whether they remain sound as circumstances change.",
    matters: "Arrangements are often made once and then left to drift. As intelligence takes part in more of them, something must keep asking whether they still hold.",
    alone: "Can be considered on its own within the Ordo software family.",
    ordoRelation: "An applied expression of General Ordo, principally its continuing-standing responsibility.",
    pairs: {
      engine: "Engine establishes present judgments; Stewardship keeps them answerable afterwards.",
      breakpoint: "Breakpoint can recognise a change consequential enough to reopen an arrangement.",
      mesh: "Mesh can connect the people responsible for an arrangement when it is reconsidered.",
    },
    signature: {
      type: "timeline", title: "Standing over time",
      steps: [
        { label: "Established", text: "An arrangement is made for good reasons." },
        { label: "Circumstances change", text: "Reality moves; the original reasons may no longer hold." },
        { label: "Reconsidered", text: "Its standing is examined again." },
        { label: "Retained or revised", text: "It keeps its standing, or is revised or withdrawn." },
      ],
    },
    placeholder: { label: "Ordo Stewardship continuing-standing illustration", note: "Arrangements retaining or losing standing over time" },
  },
  breakpoint: {
    id: "breakpoint", index: "03", name: "Breakpoint", responsibility: "Consequential recognition", family: "independent", hue: "#d2642c", path: "/enterprise/breakpoint",
    statement: "Recognises when something consequential has occurred, so that it does not pass unnoticed.",
    concerns: "The moments within ordinary work when something consequential happens and should be recognised as such.",
    matters: "Consequences are often missed not because no one could respond, but because no one recognised the moment. Recognition is its own responsibility.",
    alone: "Installs independently. It does not require any other Ordo software.",
    ordoRelation: "Draws on the shared foundation but is not a subdivision of General Ordo. It contributes a distinct recognition responsibility.",
    pairs: {
      engine: "What Breakpoint recognises can be brought to Engine for present judgment.",
      stewardship: "A recognised change can prompt Stewardship to reconsider an arrangement.",
      mesh: "Mesh can connect a recognised moment to the people who need to know.",
    },
    signature: {
      type: "threshold", title: "Recognition",
      steps: [
        { label: "Ordinary course", text: "Work proceeds as expected." },
        { label: "Breakpoint", text: "Something consequential is recognised." },
        { label: "Consequential attention", text: "The moment receives the attention it warrants." },
      ],
    },
    placeholder: { label: "Breakpoint product imagery", note: "Supplied by the founder in Phase 2" },
  },
  mesh: {
    id: "mesh", index: "04", name: "Mesh", responsibility: "Connected participation", family: "independent", hue: "#2e9c7e", path: "/enterprise/mesh",
    statement: "Connects the people and responsibilities that participate in shared work.",
    concerns: "How the people in an arrangement are connected to one another, to their responsibilities, and to the work they share.",
    matters: "Work increasingly involves people and intelligent systems acting together. Participation that is not connected becomes fragmented.",
    alone: "Installs independently. It does not require any other Ordo software.",
    ordoRelation: "Draws on the shared foundation but is not a subdivision of General Ordo. It contributes a distinct participation responsibility.",
    pairs: {
      engine: "Mesh can connect the people who carry out what Engine judges warranted.",
      stewardship: "Mesh can bring the right participants together when Stewardship reopens an arrangement.",
      breakpoint: "Breakpoint can route a recognised moment through the connections Mesh maintains.",
    },
    signature: {
      type: "participation", title: "Connected participation",
      steps: [
        { label: "Participants", text: "The people and systems taking part." },
        { label: "Responsibilities", text: "What each is answerable for." },
        { label: "Shared work", text: "The work they carry together." },
      ],
    },
    placeholder: { label: "Mesh working-participation illustration", note: "People and responsibilities in shared work" },
  },
};
